import { useRef, useState } from 'react';
import { sendEnquiry, EnquiryError, type EnquiryType } from '@/services/enquiry';

const FALLBACK_EMAIL = 'contact@didiers.co.uk';

interface Props {
  type: EnquiryType;
  /** Innovation: product name/description sent for context. */
  product?: string;
  /** Show a free-text message field (general / innovation). */
  withMessage?: boolean;
  /** Show a CV upload field (careers). */
  withCv?: boolean;
}

interface FieldErrors {
  name?: boolean;
  email?: boolean;
  message?: boolean;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function EnquiryForm({ type, product, withMessage = true, withCv = false }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [cv, setCv] = useState<File | null>(null);
  const [hp, setHp] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [fallback, setFallback] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Honeypot tripped — pretend success, send nothing.
    if (hp) {
      setStatus('sent');
      return;
    }
    const er: FieldErrors = {};
    if (!name.trim()) er.name = true;
    if (!EMAIL_RE.test(email)) er.email = true;
    if (withMessage && type === 'general' && !message.trim()) er.message = true;
    setErrors(er);
    if (Object.keys(er).length) return;

    setStatus('sending');
    setErrorMsg('');
    setFallback(false);
    try {
      await sendEnquiry({ type, name: name.trim(), email: email.trim(), phone: phone.trim(), message: message.trim(), product, cv });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      if (err instanceof EnquiryError && err.status === 503) {
        setFallback(true);
        setErrorMsg('Online enquiries are temporarily unavailable.');
      } else {
        setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      }
    }
  };

  if (status === 'sent') {
    return (
      <div className="enq-success">
        <h4>Thank you, {name.split(' ')[0] || 'there'}.</h4>
        <p>Your enquiry has been sent. Our team will be in touch shortly.</p>
      </div>
    );
  }

  return (
    <form className="enq-form" onSubmit={submit} noValidate>
      <input
        className={'enq-input' + (errors.name ? ' invalid' : '')}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        aria-label="Name"
      />
      <input
        className={'enq-input' + (errors.email ? ' invalid' : '')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        type="email"
        aria-label="Email"
      />
      <input
        className="enq-input"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="Telephone"
        type="tel"
        aria-label="Telephone"
      />
      {withMessage && (
        <textarea
          className={'enq-input' + (errors.message ? ' invalid' : '')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Your message"
          rows={4}
          aria-label="Your message"
        />
      )}
      {withCv && (
        <div className="enq-file">
          <button type="button" className="enq-file-btn" onClick={() => fileRef.current?.click()}>
            {cv ? 'Change CV' : 'Attach your CV'}
          </button>
          <span className="enq-file-name">{cv ? cv.name : 'PDF or Word, up to 8 MB'}</span>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.rtf,.odt"
            hidden
            onChange={(e) => setCv(e.target.files?.[0] ?? null)}
          />
        </div>
      )}

      {/* Honeypot — hidden from users; bots fill it and get silently dropped. */}
      <input
        className="enq-hp"
        type="text"
        name="hnpt"
        tabIndex={-1}
        autoComplete="new-password"
        aria-hidden="true"
        value={hp}
        onChange={(e) => setHp(e.target.value)}
      />

      {status === 'error' && (
        <p className="enq-error">
          {errorMsg}{' '}
          {fallback && (
            <>
              Please email us at <a href={`mailto:${FALLBACK_EMAIL}`}>{FALLBACK_EMAIL}</a>.
            </>
          )}
        </p>
      )}

      <button className="enq-send" type="submit" disabled={status === 'sending'}>
        {status === 'sending' ? 'Sending…' : 'Send'}
      </button>
    </form>
  );
}
